import CustomButton from "components/Button/CustomButton";
import PageLoader from "components/Loader/PageLoader";
import Common from "helpers/Common";
import { notificationMessages } from "helpers/constantMessages";
import { URL_INVENTORY_PRODUCTS_LIST } from "helpers/Paths";
import { useAzureStore } from "hooks/Azure";
import { useInventoryStore } from "hooks/Inventory";

import { useLoginStore } from "hooks/Login";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FaCheck, FaTrashCan } from "react-icons/fa6";
import { MdOutlineFileUpload } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
    Card,
    CardBody,
    Col,
    Row,
    Spinner,
} from "reactstrap";
import FINAL_STEP_IMG from "assets/img/icons/blue-right-tick-img.png";

const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "30px",
    borderWidth: 2,
    borderRadius: 20,
    borderColor: "#434CE6",
    borderStyle: "dashed",
    backgroundColor: "#F5F6FD",
    color: "#444444",
    margin: "20px 0 30px",
    outline: "none",
    transition: "border .24s ease-in-out",
};

const focusedStyle = {
    borderColor: "#2196f3",
};

const acceptStyle = {
    borderColor: "#00e676",
};

const rejectStyle = {
    borderColor: "#ff1744",
};

const AddInventoryByPDFFile = () => {
    const Steps = [
        {
            step: 1,
            label: "File Upload",
        },
        {
            step: 2,
            label: "Submit",
        }
    ];

    const navigate = useNavigate();

    const [{}, {}, { setAlerts }] = useLoginStore();
    const [
        {},
        {},
        { uploadInventoryByPDFFile }
    ] = useInventoryStore();
    const [{}, { fetchInventorySampleFromAzure }, {}] = useAzureStore();

    const [uploadFileList, setUploadFileList] = useState([]);
    const [loader, setLoader] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const onDrop = useCallback(async (acceptedFiles) => {
        setUploadFileList(acceptedFiles);
    }, []);

    const deleteFile = () => {
        setUploadFileList([]);
    };
    let loaderLocalStorage = localStorage.getItem("internalServerError");
    
    useEffect(() => {
        const handleStorageChange = () => {
            if (loaderLocalStorage == "true") {
                setLoader(false);
                localStorage.setItem("internalServerError", "false");
            }
        };

        // Check immediately on component mount
        handleStorageChange();

        // Add event listener for storage changes
        window.addEventListener("storage", handleStorageChange);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [loaderLocalStorage]);

    const uploadPDFFile = async (e) => {
        setLoader(true);
        e.preventDefault();

        try {
            if (!uploadFileList || uploadFileList.length === 0) {
                setAlerts({
                    type: "danger",
                    message: "Please select a file to upload",
                });
                setLoader(false);
                return;
            }

            const allowedExtensions = [".pdf", ".xlsx", ".csv", ".jpg", ".jpeg", ".png", ".bmp"];
            const fileExtension = uploadFileList[0].name.split(".").pop().toLowerCase();

            if (!allowedExtensions.includes(`.${fileExtension}`)) {
                setAlerts({
                    type: "danger",
                    message: `Invalid file type: ${uploadFileList[0].name}. Only PDF, XLSX, CSV, and Image (JPG, JPEG, PNG, BMP) files are allowed.`,
                });
                setLoader(false);
                return;
            }

            // Create form data directly with the file
            const formData = new FormData();
            formData.append("file", uploadFileList[0]);


            // Call the API directly with form data
            const uploadResult = await uploadInventoryByPDFFile(formData);

            if (uploadResult) {
                await setAlerts({
                    type: "success",
                    message: notificationMessages.addInventorySuccess,
                });
                setCurrentStep(2);
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            await setAlerts({
                type: "danger",
                message:
                    error?.responseException?.exceptionMessage?.errors?.$?.[0] ||
                    error?.responseException?.exceptionMessage?.errors?.productInventoryMasterList?.[0] ||
                    "Error uploading file",
            });
        } finally {
            setLoader(false);
        }
    };

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        onDrop: (acceptedFiles, fileRejections) => {
            if (fileRejections && fileRejections.length > 0) {
                fileRejections.forEach((file) => {
                    file.errors.forEach(async (err) => {
                        if (err.code === "file-invalid-type") {
                            await setAlerts({
                                type: "danger",
                                message: `Invalid file type: ${file.file.name}. Only Image (JPG, JPEG, PNG, BMP), CSV, PDF, and XLSX files are allowed.`,
                            });
                        }

                        if (err.code === "too-many-files") {
                            await setAlerts({
                                type: "danger",
                                message: err.message,
                            });
                        }
                    });
                });
            }

            if (acceptedFiles && acceptedFiles.length > 0) {
                onDrop(acceptedFiles);
            }
        },
        accept: {
            "application/pdf": [".pdf"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "text/csv": [".csv"],
            "image/*": [".jpg", ".jpeg", ".png", ".bmp"],
        },
        maxFiles: 1,
    });

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    return (
        <>
            {loader ? (
                <PageLoader />
            ) : (
                <div className="content-dashboard-inventory">
                    <div className="title-route-container menu-title-div">
                        <h4 className="menu-title-h">Inventory / Add Inventory By File </h4>
                    </div>

                    <Card className="h-100">
                        <CardBody>

                            <div className="d-flex align-items-center justify-content-center">
                                <div className="inventory-stepper-container">
                                    {Steps?.map((step, i) => (
                                        <div
                                            key={i}
                                            className={`inventory-step-item ${
                                                currentStep === i + 1 && "inventory-active-step"
                                            } ${
                                                (i + 1 < currentStep || currentStep === 2) &&
                                                "inventory-complete-step"
                                            } `}
                                        >
                                            <div
                                                className="inventory-step"
                                                style={{
                                                    backgroundColor: "#434CE6",
                                                }}
                                            >
                                                {i + 1 < currentStep || currentStep === 2 ? (
                                                    <FaCheck
                                                        style={{
                                                            backgroundColor: "#434CE6",
                                                        }}
                                                        size={24}
                                                    />
                                                ) : (
                                                    i + 1
                                                )}
                                            </div>
                                            <p>{step.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="container-fluid px-0">
                                {currentStep === 1 && (
                                    <>
                                        <div {...getRootProps({ style })}>
                                            <input {...getInputProps()} />
                                            {isDragActive ? (
                                                <p>Drop the file here ...</p>
                                            ) : (
                                                <div
                                                    className="dropbox-content-container"
                                                    style={{
                                                        width: "80%",
                                                        gap: "20px",
                                                    }}
                                                >
                                                    <MdOutlineFileUpload
                                                        size={80}
                                                        color="#434CE6"
                                                        className="dropbox-upload-icon"
                                                    />
                                                    <div className="">
                                                        <p className="file-drop-title">"Browse file"</p>
                                                        <p className="file-drop-desc">
                                                            Supported formats: PDF, XLSX, CSV, Images (JPG, JPEG, PNG, BMP)
                                                        </p>
                                                        <em>(Only 1 file at a time you can drop here)</em>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="">
                                            {uploadFileList &&
                                                uploadFileList.length > 0 && (
                                                    <Row>
                                                        <Col
                                                            sm="12"
                                                            md={{
                                                                size: 6,
                                                                offset: 3,
                                                            }}
                                                        >
                                                            <div
                                                                className="w-100 d-flex align-items-center justify-content-between"
                                                                key={uploadFileList[0].path}
                                                            >
                                                                <p className="m-0">
                                                                    {uploadFileList[0].path} - {uploadFileList[0].size} bytes
                                                                </p>
                                                                <FaTrashCan
                                                                    size={15}
                                                                    onClick={deleteFile}
                                                                    style={{
                                                                        cursor: "pointer",
                                                                    }}
                                                                />
                                                            </div>
                                                            <div
                                                                className="d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    margin: "20px 0",
                                                                }}
                                                            >
                                                                <CustomButton
                                                                    label={
                                                                        loader ? (
                                                                            <Spinner size="sm" color="light" />
                                                                        ) : (
                                                                            "Upload File"
                                                                        )
                                                                    }
                                                                    onClick={uploadPDFFile}
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                )}
                                        </div>
                                    </>
                                )}

                                {currentStep === 2 && (
                                    <div className="w-100 d-flex flex-column justify-content-center align-items-center gap-3">
                                        <img
                                            src={FINAL_STEP_IMG}
                                            alt="Product Added"
                                            style={{
                                                width: "100px",
                                                objectFit: "contain",
                                            }}
                                        />
                                        <p
                                            style={{
                                                fontSize: "20px",
                                                fontWeight: "500",
                                                color: "#747474",
                                                marginTop: "20px",
                                            }}
                                        >
                                            File Uploaded Successfully
                                        </p>
                                        <CustomButton
                                            label="Back To Inventory"
                                            onClick={() =>
                                                navigate(`/admin${URL_INVENTORY_PRODUCTS_LIST}`)
                                            }
                                            style={{
                                                margin: "20px 0",
                                                backgroundColor: "#555555",
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </div>
            )}
        </>
    );
};

export default AddInventoryByPDFFile;